import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { ChannelMember, Message } from '../../shared/models/chat.models';
import { ChatService } from '../../shared/services/chat.service';

@Component({
  selector: 'app-message-item',
  templateUrl: './message-item.component.html',
  styleUrls: ['./message-item.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ButtonModule, DialogModule, TooltipModule],
})
export class MessageItemComponent implements OnInit {
  @Input() message: Message | null = null;
  @Input() members: ChannelMember[] = [];
  @Input() showAvatar = true;
  @Input() showSender = true;
  @Input() compact = false;
  /** When false (1:1), avatar and sender name are hidden in the message list. */
  @Input() isGroupChat = false;
  @Output() openThread = new EventEmitter<void>();
  @Output() react = new EventEmitter<string>();
  @Output() retry = new EventEmitter<void>();

  showActions = false;
  attachmentPresignedUrl: string | null = null;
  previewDialogVisible = false;
  downloadingAttachment = false;
  currentUserId: number | null = null;

  readonly COMMON_EMOJIS = ['👍', '❤️', '😂', '🎉', '✅', '👀'];

  constructor(
    private chatService: ChatService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.currentUserId = this.resolveCurrentUserId();
    if (!this.message) return;
    if (this.message.attachment_url) {
      this.chatService.getPresignedDownloadUrl(this.message.attachment_url).subscribe({
        next: (res) => {
          this.attachmentPresignedUrl = res.presigned_url;
          this.cdr.markForCheck();
        },
      });
    }
  }

  get senderName(): string {
    if (!this.message) return '';
    if (this.message.sender_name) return this.message.sender_name;

    const member = this.members.find(
      (m) => m.user_id === this.message!.sender_id
    );

    return member?.user_name ?? `User ${this.message.sender_id}`;
  }

  get senderInitials(): string {
    return this.senderName
      .split(' ')
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  private parseUtcDate(raw: string): Date {
    // MySQL returns "2026-03-17 14:30:00" — no T, no Z.
    // Without normalization JS may parse it as local time instead of UTC.
    // Replace space with T and append Z so it is always treated as UTC.
    const normalized = raw.replace(' ', 'T').replace(/Z?$/, 'Z');
    return new Date(normalized);
  }

  get formattedTime(): string {
    if (!this.message) return '';
    const d = this.parseUtcDate(this.message.created_at);
    return d.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  get formattedDate(): string {
    const d = this.parseUtcDate(this.message.created_at);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Today';
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString();
  }

  get isImage(): boolean {
    if (this.message?.attachment_type?.startsWith('image/')) return true;
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(this.message?.attachment_name ?? '');
  }

  get isPdf(): boolean {
    if (this.message?.attachment_type === 'application/pdf') return true;
    return /\.pdf$/i.test(this.message?.attachment_name ?? '');
  }

  get isVideo(): boolean {
    if (this.message?.attachment_type?.startsWith('video/')) return true;
    return /\.(webm|mp4|mov|avi|mkv|m4v|ogv)$/i.test(this.message?.attachment_name ?? '');
  }

  get reactionEntries(): { emoji: string; count: number; hasMe: boolean }[] {
    return Object.entries(this.message.reactions || {}).map(([emoji, userIds]) => ({
      emoji,
      count: userIds.length,
      hasMe: false, // simplified — would need current user id to check
    }));
  }

  onReact(emoji: string): void {
    this.react.emit(emoji);
  }

  get isMine(): boolean {
    return this.currentUserId !== null && this.message?.sender_id === this.currentUserId;
  }

  get showDeliveryStatus(): boolean {
    return (
      this.isMine &&
      !!this.message?.delivery_status &&
      this.message.delivery_status !== 'sent'
    );
  }

  get deliveryLabel(): string {
    switch (this.message?.delivery_status) {
      case 'queued':
        return 'Waiting to send';
      case 'failed':
        return 'Failed to send';
      case 'sending':
        return 'Sending';
      default:
        return '';
    }
  }

  onRetryClick(event: Event): void {
    event.stopPropagation();
    this.retry.emit();
  }

  openImagePreview(event?: Event): void {
    event?.stopPropagation();
    if (!this.attachmentPresignedUrl || !this.isImage) return;
    this.previewDialogVisible = true;
    this.cdr.markForCheck();
  }

  closeImagePreview(): void {
    this.previewDialogVisible = false;
    this.cdr.markForCheck();
  }

  openAttachmentInNewTab(event?: Event): void {
    event?.stopPropagation();
    if (!this.attachmentPresignedUrl) return;
    window.open(this.attachmentPresignedUrl, '_blank', 'noopener,noreferrer');
  }

  downloadAttachment(event?: Event): void {
    event?.stopPropagation();
    if (!this.attachmentPresignedUrl || this.downloadingAttachment) return;

    const filename =
      this.message?.attachment_name ||
      this.message?.attachment_url?.split('/').pop() ||
      'download';

    this.downloadingAttachment = true;
    this.cdr.markForCheck();

    fetch(this.attachmentPresignedUrl)
      .then((res) => {
        if (!res.ok) throw new Error('Download failed');
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.download = filename;
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {
        this.openAttachmentInNewTab();
      })
      .finally(() => {
        this.downloadingAttachment = false;
        this.cdr.markForCheck();
      });
  }

  private resolveCurrentUserId(): number | null {
    try {
      const raw = localStorage.getItem('optplanner_user');
      if (!raw) return null;
      const user = JSON.parse(raw);
      const id = Number(user.id ?? user.user_id ?? user.userId);
      return Number.isFinite(id) ? id : null;
    } catch {
      return null;
    }
  }
}
